import React, { useEffect, useState } from 'react';
import logoWholefoods from '../../../assets/landing/logo-wholefoods.png';
import logoAngel from '../../../assets/landing/logo-angel.png';
import MobileDownloadLink from '../../../components/MobileDownloadLink';
import SectionShape from '../../../components/SectionShape';

/**
 * Hero section — the top of the landing page.
 *
 * Kept from the design:
 *  - headline / subhead / CTAs
 *  - the floating "financial address" card with 5 rows that fade in
 *    via the CSS @keyframes `heroRow` (defined in globals.css — no GSAP)
 *  - a static faint radial glow behind the card
 *
 * Cut from the design:
 *  - the scroll-morph SVG ring (needs paid GSAP MorphSVGPlugin)
 *
 * Illustrative values count up on entry and replay when Cash out is pressed.
 * The demo never initiates a payment or navigates away.
 */

const IOS_URL = 'https://apps.apple.com/us/app/kachng-digital-receipt/id6502332854';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.Gibbas.KACHNG';

const mono = "'JetBrains Mono', ui-monospace, monospace";

// One rewards-card row. `delay` drives the staggered entrance.
function CardRow({ icon, title, sub, right, delay }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        animation: `heroRow 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}s both`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {icon}
        <div>
          <div style={{ fontSize: '15px', fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{sub}</div>
        </div>
      </div>
      {right}
    </div>
  );
}

const iconWrap = (bg, children) => (
  <div
    style={{
      width: '36px',
      height: '36px',
      borderRadius: '9px',
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 'none',
    }}
  >
    {children}
  </div>
);

const pts = (txt, color) => (
  <div
    style={{
      fontFamily: mono,
      fontSize: '14px',
      fontWeight: 700,
      fontVariantNumeric: 'tabular-nums',
      color: color || 'inherit',
    }}
  >
    {txt}
  </div>
);

export default function Hero({ mobile = false }) {
  const [replay, setReplay] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (mobile) return undefined;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame;
    const start = performance.now();
    const finishIfReduced = () => {
      if (motion.matches) {
        cancelAnimationFrame(frame);
        setElapsed(2200);
      }
    };
    const tick = (now) => {
      const time = Math.min(now - start, 2200);
      setElapsed(time);
      if (time < 2200) frame = requestAnimationFrame(tick);
    };
    if (motion.matches) finishIfReduced();
    else frame = requestAnimationFrame(tick);
    motion.addEventListener('change', finishIfReduced);
    return () => {
      cancelAnimationFrame(frame);
      motion.removeEventListener('change', finishIfReduced);
    };
  }, [replay, mobile]);

  const count = (value, delay = 300) => {
    const progress = Math.min(1, Math.max(0, (elapsed - delay) / 1100));
    return Math.round(value * (1 - Math.pow(1 - progress, 3)));
  };
  const replayCard = () => {
    setElapsed(0);
    setReplay((value) => value + 1);
  };

  return (
    <header className="landing-hero"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(40px,5vw,64px)',
        padding: 'clamp(76px,8vw,96px) clamp(20px,5vw,56px) clamp(96px,11vw,168px)',
        alignItems: 'center',
        maxWidth: 'var(--max-width)',
        margin: '0 auto',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Left column — copy + CTAs */}
      <div className="hero-copy" style={{ containerType: 'inline-size', minWidth: 0, flex: '1 1 420px' }}>
        <h1
          style={{
            margin: '0 0 clamp(22px,2.2vw,30px)',
            fontSize: 'clamp(28px,8.7cqw,64px)',
            lineHeight: 1.06,
            fontWeight: 800,
            letterSpacing: '-0.03em',
          }}
        >
          <span style={{ whiteSpace: 'nowrap', color: 'var(--text-300)' }}>Every purchase</span>
          <br />
          <span style={{ whiteSpace: 'nowrap', color: 'var(--text-300)' }}>
            tells a <span style={{ color: 'var(--kachng-green)' }}>story</span>.
          </span>
          <br />
          <span style={{ whiteSpace: 'nowrap', display: 'inline-block', marginTop: '0.52em' }}>
            KACHNG <br />
            remembers it.
          </span>
        </h1>

        <p
          style={{
            margin: '0 0 32px',
            fontSize: 'clamp(17px,1.5vw,19px)',
            lineHeight: 1.55,
            color: 'var(--text-300)',
            maxWidth: '52ch',
          }}
        >
          Turn the receipts you already <br />
          receive into spending intelligence.
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '14px',
          }}
        >
          {mobile ? <MobileDownloadLink source="hero" /> : <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a
              href={IOS_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'var(--kachng-green)',
                color: 'var(--bg-ink)',
                fontWeight: 700,
                fontSize: '16px',
                padding: '16px 32px',
                borderRadius: '8px',
                minHeight: '44px',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
                width: 'max-content',
              }}
            >
              Download for iOS
            </a>
            <a
              href={ANDROID_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'var(--green-accent-bg)',
                color: 'var(--kachng-green)',
                fontWeight: 700,
                fontSize: '16px',
                padding: '16px 32px',
                borderRadius: '8px',
                minHeight: '44px',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
                width: 'max-content',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--green-accent-bg-hi)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--green-accent-bg)')}
            >
              Download for Android
            </a>
          </div>}
          <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
            Takes 30 seconds. Nothing else changes.
          </span>
        </div>
      </div>

      {/* Desktop demo is unmounted on phones so its animation and images stay idle. */}
      {!mobile && <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          flex: '0 1 366px',
          minWidth: 'min(100%,300px)',
          marginLeft: 'auto',
          marginRight: 'clamp(20px, 6vw, 140px)',
        }}
      >
        {/* Rhyming ring behind the card (bookends with Close) */}
        <SectionShape
          kind="ring"
          size={640}
          bright
          draw
          spin={false}
          blur={2}
          opacity={0.55}
          position={{ marginRight: '140px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
        />

        {/* Static faint glow (ring cut) */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 'min(760px,110vw)',
            height: 'min(760px,110vw)',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(0,230,118,0.085) 0%, rgba(0,230,118,0.028) 45%, transparent 68%)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            background: 'radial-gradient(120% 90% at 50% 0%, var(--card-grad-top) 0%, var(--card-grad-bottom) 65%)',
            border: '1px solid var(--border-soft)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            borderRadius: '22px',
            padding: '24px 24px 20px',
            width: 'min(366px,100%)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontFamily: mono,
              fontSize: '11px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              fontWeight: 500,
              color: 'var(--text-muted)',
              marginBottom: '10px',
            }}
          >
            Your financial address
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              background: 'var(--bg-ink)',
              border: '1px solid var(--border-input)',
              borderRadius: '12px',
              padding: '14px 18px',
              marginBottom: '24px',
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '0.01em' }}>
              maya<span style={{ color: 'var(--text-muted)' }}>@kachng.co</span>
            </span>
            <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>give this at checkout</span>
          </div>

          <div key={replay} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
            <CardRow
              delay={0.3}
              icon={iconWrap(
                'var(--green-accent-bg)',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00E676" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 2h12v20l-2-1.5L14 22l-2-1.5L10 22l-2-1.5L6 22z" />
                  <path d="M9.5 7.5h5" />
                  <path d="M9.5 11.5h5" />
                </svg>
              )}
              title="Receipt received"
              sub="Uniqlo · 4 line items processed"
              right={pts(`+${count(50, 300)} pts`)}
            />

            <CardRow
              delay={0.5}
              icon={
                <img
                  src={logoWholefoods}
                  alt="Whole Foods Market"
                  style={{ width: '36px', height: '36px', borderRadius: '9px', flex: 'none' }}
                />
              }
              title="Whole Foods Market"
              sub="purchase remembered"
              right={pts(`+${count(50, 500)} pts`)}
            />

            <CardRow
              delay={0.7}
              icon={iconWrap(
                'rgba(255,255,255,0.08)',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9CEC9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2.5 5v6h6" />
                  <path d="M4.5 16a9 9 0 1 0 2.13-9.36L2.5 11" />
                </svg>
              )}
              title="Return reminder"
              sub="Uniqlo jacket · window closing"
              right={pts(`${count(7, 700)} days`)}
            />

            <CardRow
              delay={0.9}
              icon={
                <img
                  src={logoAngel}
                  alt="Angel Fruits"
                  style={{ width: '36px', height: '36px', borderRadius: '9px', flex: 'none' }}
                />
              }
              title="Angel Fruits"
              sub="new merchant discovered · bonus"
              right={
                <div style={{ textAlign: 'right' }}>
                  {pts(`+${count(50, 900)} pts`)}
                  <div style={{ fontSize: '12px', color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums' }}>
                    +{count(100, 900)} pts
                  </div>
                </div>
              }
            />

            <CardRow
              delay={1.1}
              icon={iconWrap(
                'var(--kachng-green)',
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#052012" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 13h4l3-8 4 16 3-8h4" />
                </svg>
              )}
              title="New insight"
              sub="coffee trending this month"
              right={
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontFamily: mono, fontSize: '14px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--kachng-green)' }}>
                  +{count(18, 1100)}%
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00E676" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                    <path d="M7 17L17 7" />
                    <path d="M9 7h8v8" />
                  </svg>
                </div>
              }
            />
          </div>

          {/* Balance footer */}
          <div
            style={{
              borderTop: '1px solid var(--border-hairline)',
              paddingTop: '20px',
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '18px',
            }}
          >
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Rewards balance</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <span style={{ fontFamily: mono, fontSize: '22px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', color: 'var(--text-300)' }}>
                ${(count(1770) / 100).toFixed(2)}
              </span>
              <span style={{ fontFamily: mono, fontSize: '13px', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {count(8850).toLocaleString('en-US')} pts
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={replayCard}
            aria-describedby="hero-demo-note"
            style={{
              background: 'transparent',
              fontFamily: 'inherit',
              cursor: 'pointer',
              display: 'block',
              width: '100%',
              textAlign: 'center',
              border: '1px solid var(--border-soft)',
              color: 'var(--text-300)',
              fontWeight: 600,
              fontSize: '14px',
              padding: '12px 0',
              borderRadius: '999px',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s, color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--kachng-green)';
              e.currentTarget.style.color = 'var(--kachng-green)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-soft)';
              e.currentTarget.style.color = 'var(--text-300)';
            }}
          >
            Cash out $17.70
          </button>
          <div id="hero-demo-note" style={{ marginTop: '10px', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
            Interactive demo · Cash out replays the card
          </div>
        </div>
      </div>}
    </header>
  );
}